// pages/ResetPasswordPage.jsx
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaCheck, FaTimes } from "react-icons/fa";
import adminApi from "../services/adminApi";
import { getErrorMessage } from "../utils/apiError";
import { checkPasswordComplexity, PASSWORD_REQUIREMENTS } from "../utils/passwordValidator";
import "../adminStyle/adminLoginPage.css";
import illustration from "../images/illustration1.png";
import logo from "../images/logo.png";

export default function ResetPasswordPage() {
  const [form, setForm] = useState({ token: "", new_password: "", confirm: "" });
  const [showPw, setShowPw]   = useState(false);
  const [showCf, setShowCf]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");
  const [success, setSuccess] = useState(false);
  const navTimerRef = useRef(null);

  const navigate = useNavigate();

  const pwValidation = checkPasswordComplexity(form.new_password);
  const passwordsMatch = form.new_password.length > 0 && form.new_password === form.confirm;

  useEffect(() => {
    return () => {
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
    };
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!pwValidation.isValid) {
      setError(pwValidation.error);
      return;
    }
    if (form.new_password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await adminApi.post("/api/admin/reset-password-token", {
        token: form.token.trim(),
        new_password: form.new_password,
      });
      setSuccess(true);
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
      navTimerRef.current = setTimeout(() => navigate("/admin/login"), 2500);
    } catch (e) {
      setError(getErrorMessage(e, "Failed to reset password."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="admin-login-page">
      <div className="admin-layout">

        {/* ── LEFT SIDE — identical to login ── */}
        <div className="corner-banner">
          <div className="banner-content">
            <img src={logo} alt="Jerry's Bins" />
            <p>Jerry's Bins</p>
          </div>
          <svg
            className="corner-svg"
            width="550"
            height="320"
            viewBox="0 0 550 320"
            fill="none"
          >
            <path
              d="M 0 0 L 480 0 L 415 45 C 375 75 330 84 250 84 C 150 84 110 110 70 165 L 0 300 Z"
              fill="#1B2A4A"
            />
          </svg>
        </div>

        <div className="admin-left">
          <div className="admin-left-shape">
            <div className="admin-illustration">
              <img src={illustration} alt="Waste Management" />
            </div>
          </div>
          <svg viewBox="0 0 600 600" preserveAspectRatio="none">
            <polygon points="523,0 560,0 576,312 "          fill="#d0d5e0" stroke="#969ab6" strokeWidth="1" />
            <polygon points="576,312 487,460 475,461 "       fill="#d0d5e0" stroke="#969ab6" strokeWidth="1" />
            <polygon points="475,461 487,460 490,550 380,599 281,599 462,546" fill="#d0d5e0" stroke="#969ab6" strokeWidth="1" />
            <polygon points="490,550 430,599 380,599"        fill="#f5f6fa" stroke="#969ab6" strokeWidth="1" />
          </svg>
        </div>

        {/* ── RIGHT SIDE — reset password form ── */}
        <div className="admin-right">
          <div className="admin-right-shape">
            <svg viewBox="-60 0 600 700" preserveAspectRatio="xMidYMid meet" style={{ shapeRendering: "crispEdges" }}>
              <polygon points="0,130 120,50 310,130 130,160"          fill="#f5f6fa" stroke="#969ab6" strokeWidth="2" />
              <polygon points="120,50 340,50 310,130 "                 fill="#d0d5e0" stroke="#969ab6" strokeWidth="2" />
              <polygon points="340,50 440,80 500,220 380,180 310,130"  fill="#f5f6fa" stroke="#969ab6" strokeWidth="2" />
              <polygon points="0,130 130,160 60,290 20,400"            fill="#d0d5e0" stroke="#969ab6" strokeWidth="2" />
              <polygon points="60,290 130,160 310,130 380,180 280,310" fill="#f5f6fa" stroke="#969ab6" strokeWidth="2" />
              <polygon points="60,290 280,310 260,480 160,500 80,460 20,400" fill="#f5f6fa" stroke="#969ab6" strokeWidth="2" />
              <polygon points="280,310 400,300 420,460 260,480"        fill="#cdd0dc" stroke="#969ab6" strokeWidth="2" />
              <polygon points="380,180 500,220 500,500 420,460"        fill="#f5f6fa" stroke="#969ab6" strokeWidth="2" />
              <polygon points="20,400 80,460 20,680"                   fill="#f5f6fa" stroke="#969ab6" strokeWidth="2" />
              <polygon points="20,680 80,460 160,500 140,620"          fill="#e0e3ee" stroke="#969ab6" strokeWidth="2" />
              <polygon points="160,500 260,480 240,620 140,620"        fill="#f5f6fa" stroke="#969ab6" strokeWidth="2" />
              <polygon points="260,480 420,460 400,600 240,620"        fill="#f5f6fa" stroke="#969ab6" strokeWidth="2" />
              <polygon points="420,460 500,500 500,640 470,680 400,600" fill="#d0d5e0" stroke="#969ab6" strokeWidth="2" />
            </svg>
          </div>

          <div className="admin-login-card">
            <h2 className="admin-login-title">Reset Password</h2>

            {error && <div className="admin-login-error">{error}</div>}

            {success ? (
              <div style={{
                background: "#f0fdf4", border: "1px solid #86efac",
                color: "#15803d", borderRadius: 8,
                padding: "12px 14px", fontSize: 13, fontWeight: 500,
              }}>
                <i className="ti ti-circle-check" style={{ marginRight: 6 }} />
                Password updated! Redirecting to login…
              </div>
            ) : (
              <form className="admin-login-form" onSubmit={handleSubmit}>

                {/* Reset token */}
                <div className="admin-form-group">
                  <label>Reset Token</label>
                  <input
                    type="text"
                    placeholder="Paste your reset token"
                    autoComplete="off"
                    value={form.token}
                    onChange={(e) => setForm({ ...form, token: e.target.value })}
                    required
                    className="admin-form-input"
                  />
                </div>

                {/* New password */}
                <div className="admin-form-group">
                  <label>New Password</label>
                  <div className="admin-password-wrap">
                    <input
                      type={showPw ? "text" : "password"}
                      placeholder="At least 8 characters"
                      value={form.new_password}
                      onChange={(e) => setForm({ ...form, new_password: e.target.value })}
                      required
                      className="admin-form-input"
                    />
                    <button
                      type="button"
                      className="admin-password-toggle"
                      onClick={() => setShowPw((v) => !v)}
                    >
                      <i className={showPw ? "ti ti-eye-off" : "ti ti-eye"} />
                    </button>
                  </div>
                </div>

                {/* Confirm password */}
                <div className="admin-form-group">
                  <label>Confirm Password</label>
                  <div className="admin-password-wrap">
                    <input
                      type={showCf ? "text" : "password"}
                      placeholder="Repeat new password"
                      value={form.confirm}
                      onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                      required
                      className="admin-form-input"
                    />
                    <button
                      type="button"
                      className="admin-password-toggle"
                      onClick={() => setShowCf((v) => !v)}
                    >
                      <i className={showCf ? "ti ti-eye-off" : "ti ti-eye"} />
                    </button>
                  </div>
                </div>

                {form.new_password.length > 0 && (
                  <div className="auth-pw-checklist" style={{ background: "#f1f5f9", borderColor: "#e2e8f0" }}>
                    {PASSWORD_REQUIREMENTS.map((r) => (
                      <div
                        key={r.key}
                        className={`auth-pw-req ${pwValidation.checks[r.key] ? "met" : "unmet"}`}
                        style={{ color: pwValidation.checks[r.key] ? "#10B981" : "#64748b" }}
                      >
                        {pwValidation.checks[r.key] ? <FaCheck size={11} /> : <FaTimes size={11} />}
                        <span>{r.label}</span>
                      </div>
                    ))}
                    {form.confirm.length > 0 && (
                      <div
                        className={`auth-pw-req ${passwordsMatch ? "met" : "unmet"}`}
                        style={{ color: passwordsMatch ? "#10B981" : "#64748b" }}
                      >
                        {passwordsMatch ? <FaCheck size={11} /> : <FaTimes size={11} />}
                        <span>Passwords match</span>
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  className="admin-btn-primary"
                  disabled={loading}
                >
                  {loading ? "Updating…" : "Set New Password"}
                </button>

              </form>
            )}

            <div className="admin-login-links">
              <button
                type="button"
                className="admin-forgot-link"
                onClick={() => navigate("/admin/login")}
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}